import { ILocationItem } from "react-ui-components/components/models/ILocation"

export const URL_CONTEXT_PARAMS = {
  language: "lang",
  languageFallback: "lng",
  country: "country",
  state: "state",
  city: "city",
  zip: "zip",
} as const

type UrlContextInput = {
  language?: string | null
  location?: Partial<ILocationItem> | null
}

const sanitize = (value?: string | null): string => (value || "").trim()

export const normalizeLanguageCode = (language?: string | null): string => {
  const value = sanitize(language)
  if (!value) {
    return ""
  }
  return value.split("-")[0].toLowerCase()
}

// `users/{uid}.language` (set at registration via LanguageSelector/Register.tsx) stores the
// full language name shown in that dropdown (e.g. "English"/"Spanish"), not an i18next code —
// unlike `?lang=` URL values or `i18n.language`, which are always codes. Falls back to treating
// the input as a code if it isn't a recognized name, so an already-code-shaped value still
// resolves correctly.
const LANGUAGE_NAME_TO_CODE: Record<string, string> = {
  english: "en",
  spanish: "es",
  español: "es",
}

export const resolveStoredLanguageCode = (language?: string | null): string => {
  const value = sanitize(language)
  if (!value) {
    return ""
  }
  return LANGUAGE_NAME_TO_CODE[value.toLowerCase()] || normalizeLanguageCode(value)
}

export const normalizeLocationContext = (
  location?: Partial<ILocationItem> | null,
): ILocationItem => ({
  country: sanitize(location?.country),
  state: sanitize(location?.state),
  city: sanitize(location?.city),
  zip: sanitize((location as { zip?: string | null } | null | undefined)?.zip),
})

export const hasLocationContext = (location?: Partial<ILocationItem> | null): boolean => {
  const normalized = normalizeLocationContext(location)
  return Boolean(normalized.country || normalized.state || normalized.city || normalized.zip)
}

const toSearchParams = (search?: string | URLSearchParams): URLSearchParams => {
  if (search instanceof URLSearchParams) {
    return new URLSearchParams(search.toString())
  }
  return new URLSearchParams(search || "")
}

export const readUrlContext = (search?: string | URLSearchParams) => {
  const params = toSearchParams(search)
  const location = normalizeLocationContext({
    country: params.get(URL_CONTEXT_PARAMS.country),
    state: params.get(URL_CONTEXT_PARAMS.state),
    city: params.get(URL_CONTEXT_PARAMS.city),
    zip: params.get(URL_CONTEXT_PARAMS.zip),
  })
  const language = normalizeLanguageCode(
    params.get(URL_CONTEXT_PARAMS.language) || params.get(URL_CONTEXT_PARAMS.languageFallback),
  )

  return {
    language,
    location,
    hasLanguage: Boolean(language),
    hasLocation: hasLocationContext(location),
    hasZipParam: params.has(URL_CONTEXT_PARAMS.zip),
  }
}

export const resolveInitialLocation = (
  search?: string | URLSearchParams,
  savedLocation?: Partial<ILocationItem> | null,
): ILocationItem => {
  const normalizedSaved = normalizeLocationContext(savedLocation)
  const { location: urlLocation, hasLocation, hasZipParam } = readUrlContext(search)

  if (!hasLocation) {
    return normalizedSaved
  }

  const sameLocalityAsSaved =
    urlLocation.country === normalizedSaved.country &&
    urlLocation.state === normalizedSaved.state &&
    urlLocation.city === normalizedSaved.city

  return {
    ...urlLocation,
    zip:
      hasZipParam || !sameLocalityAsSaved
        ? urlLocation.zip
        : urlLocation.zip || normalizedSaved.zip,
  }
}

export const applyUrlContext = (params: URLSearchParams, context: UrlContextInput) => {
  const language = normalizeLanguageCode(context.language)
  const location = normalizeLocationContext(context.location)

  if (language) {
    params.set(URL_CONTEXT_PARAMS.language, language)
  } else {
    params.delete(URL_CONTEXT_PARAMS.language)
  }

  params.delete(URL_CONTEXT_PARAMS.languageFallback)

  if (location.country) {
    params.set(URL_CONTEXT_PARAMS.country, location.country)
  } else {
    params.delete(URL_CONTEXT_PARAMS.country)
  }

  if (location.state) {
    params.set(URL_CONTEXT_PARAMS.state, location.state)
  } else {
    params.delete(URL_CONTEXT_PARAMS.state)
  }

  if (location.city) {
    params.set(URL_CONTEXT_PARAMS.city, location.city)
  } else {
    params.delete(URL_CONTEXT_PARAMS.city)
  }

  if (location.zip) {
    params.set(URL_CONTEXT_PARAMS.zip, location.zip)
  } else {
    params.delete(URL_CONTEXT_PARAMS.zip)
  }

  return params
}

const isAbsoluteUrl = (url: string): boolean => /^[a-z][a-z\d+\-.]*:/i.test(url) || url.startsWith("//")

export const buildUrlWithContext = (url: string, context: UrlContextInput): string => {
  if (!url) {
    return url
  }

  try {
    const baseOrigin = typeof window !== "undefined" ? window.location.origin : "https://winfacil.local"
    const parsedUrl = new URL(url, baseOrigin)
    const params = new URLSearchParams(parsedUrl.search)
    applyUrlContext(params, context)

    const query = params.toString()
    parsedUrl.search = query ? `?${query}` : ""

    if (!isAbsoluteUrl(url) && !url.startsWith("#")) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
    }

    return parsedUrl.toString()
  } catch {
    return url
  }
}
